import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, ChevronUp, Plus, X, Check } from 'lucide-react';

// Base options
const BASE_OPTIONS = ['Root', 'Hot', 'Fills', 'Cap', 'Others'];

const CustomRootSelect = ({ value, onChange, placeholder = 'Select...' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isInputMode, setIsInputMode] = useState(false);
    const [customInput, setCustomInput] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Maintain a unique list of options including any custom value passed in 'value'
    // If 'value' is something not in BASE_OPTIONS, we treat it as a custom added option
    // but we DON'T add it if it is empty or 'Others' (which is just a trigger)
    const options = useMemo(() => {
        const isCustom = value && !BASE_OPTIONS.includes(value);
        if (isCustom) {
            return [...BASE_OPTIONS.filter(o => o !== 'Others'), value, 'Others'];
        }
        return BASE_OPTIONS;
    }, [value]);

    // Sync mode: If value is 'Others' (unlikely to persist as value, but transiently) or explicitly set to input logic
    useEffect(() => {
        if (value === 'Others') {
            setIsInputMode(true);
            setCustomInput('');
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                if (isInputMode) {
                    setIsInputMode(false);
                    setCustomInput('');
                    if (value === 'Others') {
                        onChange('');
                    }
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isInputMode, value, onChange]);

    useEffect(() => {
        if (isInputMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isInputMode]);

    const handleAdd = (e) => {
        e.stopPropagation();
        if (customInput.trim()) {
            onChange(customInput.trim());
            setIsInputMode(false);
            setCustomInput('');
            setIsOpen(false);
        }
    };

    const handleCancelInput = (e) => {
        e.stopPropagation();
        setIsInputMode(false);
        setCustomInput('');
        if (value === 'Others') {
            onChange('');
        }
        setIsOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleAdd(e);
        } else if (e.key === 'Escape') {
            handleCancelInput(e);
        }
    };

    const handleOptionClick = (opt) => {
        if (opt === 'Others') {
            onChange('Others'); // This triggers the useEffect to switch to InputMode
        } else {
            onChange(opt);
            setIsOpen(false);
            setIsInputMode(false);
        }
    };

    if (isInputMode) {
        return (
            <div ref={containerRef} style={{ position: 'relative', width: '160px', minWidth: '160px' }}>
                <div style={{
                    position: 'relative',
                    width: '100%',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #3B82F6',
                    padding: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxSizing: 'border-box',
                    height: '38px' // Match render height
                }}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={customInput}
                        onChange={(e) => setCustomInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Station Name..."
                        style={{
                            flex: 1,
                            width: '0',
                            height: '100%',
                            padding: '0 4px',
                            fontSize: '13px',
                            border: 'none',
                            color: '#374151',
                            outline: 'none',
                            background: 'transparent'
                        }}
                    />
                    <button
                        onClick={handleAdd}
                        style={{
                            backgroundColor: '#EFF6FF',
                            color: '#3B82F6',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s'
                        }}
                        title="Add"
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DBEAFE'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EFF6FF'}
                    >
                        <Check size={16} />
                    </button>
                    <button
                        onClick={handleCancelInput}
                        style={{
                            backgroundColor: 'transparent',
                            color: '#9CA3AF',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'color 0.2s'
                        }}
                        title="Cancel"
                        onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '160px', minWidth: '160px', textAlign: 'left' }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    minHeight: '38px',
                    height: '38px',
                    boxSizing: 'border-box',
                    padding: '4px 12px',
                    fontSize: '13px',
                    border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                    color: value && value !== 'Others' ? '#374151' : '#9CA3AF',
                    outline: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s',
                    overflow: 'hidden'
                }}
                title={value}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {value === 'Others' ? placeholder : (value || placeholder)}
                </span>
                {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    padding: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                }}>
                    {options.map((opt) => (
                        <div
                            key={opt}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOptionClick(opt);
                            }}
                            style={{
                                padding: '8px 10px',
                                fontSize: '13px',
                                color: '#374151',
                                backgroundColor: value === opt ? '#F3F4F6' : 'transparent',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                marginBottom: '1px',
                                transition: 'background-color 0.2s',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === opt ? '#F3F4F6' : 'transparent'}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                                {opt}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomRootSelect;
